import { useLazyQuery, useMutation } from '@apollo/client';
import * as Tabs from '@radix-ui/react-tabs';
import { Settings, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { CIRCLES } from '../../constants/circles.constants';
import { SHARED_META } from '../../constants/share.constants';
import type {
  CircleFieldsFragment,
  CircleStoryFieldsFragment,
} from '../../gql/graphql';
import { DELETE_CIRCLE } from '../../graphql/resolvers/circleMutations/deleteCircle.gql';
import { GET_CIRCLE } from '../../graphql/resolvers/circleQueries/getCircle.gql';
import CircleStoryRow from './CircleStoryRow';
import ManageCircleModal from './ManageCircleModal';

interface CircleCardProps {
  circle: CircleFieldsFragment;
}

const CircleCard = ({ circle }: CircleCardProps) => {
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [sharedStories, setSharedStories] = useState<
    CircleStoryFieldsFragment[]
  >([]);
  const [isManageOpen, setIsManageOpen] = useState(false);

  const [fetchCircleDetail, { loading: detailLoading }] = useLazyQuery(
    GET_CIRCLE,
    {
      variables: { id: circle.id },
      onCompleted: (data) => {
        if (data?.circle?.sharedStories) {
          setSharedStories(data.circle.sharedStories);
        }
        setStoriesLoaded(true);
      },
    },
  );

  const [deleteCircleMutation] = useMutation(DELETE_CIRCLE, {
    update(cache) {
      cache.evict({
        id: cache.identify({ __typename: 'Circle', id: circle.id }),
      });
      cache.gc();
    },
  });

  const handleTabChange = (value: string) => {
    if (value === 'stories' && !storiesLoaded) {
      fetchCircleDetail();
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCircleMutation({ variables: { id: circle.id } });
    } catch (err) {
      console.error(CIRCLES.ERRORS.FAILED_DELETE, err);
    }
  };

  return (
    <div className="circle-card">
      <div className="circle-card__header">
        <h3 className="circle-card__name">{circle.name}</h3>
        <span className="circle-card__meta">
          {circle.memberCount}{' '}
          {circle.memberCount === 1 ? SHARED_META.MEMBER : SHARED_META.MEMBERS}
        </span>
        <button
          type="button"
          className="circle-card__icon-btn"
          onClick={() => setIsManageOpen(true)}
          aria-label={CIRCLES.ARIA.MANAGE}
        >
          <Settings size={16} />
        </button>
        <button
          type="button"
          className="circle-card__icon-btn circle-card__icon-btn--danger"
          onClick={handleDelete}
          aria-label={CIRCLES.ARIA.DELETE}
        >
          <Trash2 size={16} />
        </button>
      </div>

      <Tabs.Root defaultValue="members" onValueChange={handleTabChange}>
        <Tabs.List className="circle-card__tabs" aria-label={CIRCLES.ARIA.TABS}>
          <Tabs.Trigger className="circle-card__tabs__tab" value="members">
            {CIRCLES.CARD.TAB_MEMBERS}
          </Tabs.Trigger>
          <Tabs.Trigger className="circle-card__tabs__tab" value="stories">
            {CIRCLES.CARD.TAB_STORIES}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="members" className="circle-card__body">
          <div className="circle-card__members">
            {circle.members.length > 0 ? (
              circle.members.map((username) => (
                <span key={username} className="chip">
                  {username}
                </span>
              ))
            ) : (
              <span className="circle-card__meta">
                {circle.memberCount} {CIRCLES.CARD.MEMBERS_EXPAND}
              </span>
            )}
          </div>
        </Tabs.Content>

        <Tabs.Content value="stories" className="circle-card__body">
          {detailLoading && (
            <p className="circle-card__stories-empty">
              {CIRCLES.CARD.STORIES_LOADING}
            </p>
          )}
          {storiesLoaded && sharedStories.length === 0 && (
            <p className="circle-card__stories-empty">
              {CIRCLES.CARD.STORIES_EMPTY}
            </p>
          )}
          {sharedStories.map((story) => (
            <CircleStoryRow
              key={story.storyId}
              story={story}
              totalMembers={circle.memberCount}
            />
          ))}
        </Tabs.Content>
      </Tabs.Root>

      <ManageCircleModal
        open={isManageOpen}
        onClose={() => setIsManageOpen(false)}
        circle={circle}
      />
    </div>
  );
};

export default CircleCard;
